import { z } from 'zod';
import { AssessmentConfigSchema } from '../assessment/schema.js';
import { technicalSupportAssessment } from '../assessment/bank.js';
import { LANGUAGE_CODES, type LanguageCode } from '../lang.js';

/**
 * The qualification contract for one job. Everything the AI interviewer asks,
 * everything the scorer assesses, and every rule that decides "qualified" is
 * declared here, per job, and editable by the recruiter.
 *
 * The split matters:
 *  - competencies are JUDGED by the model (1-5 with quoted evidence);
 *  - facts are EXTRACTED by the model (notice period, CTC, location ...);
 *  - knockouts and thresholds are APPLIED BY CODE to those facts and scores.
 * The model never decides the verdict on its own arithmetic.
 */

const key = z
  .string()
  .regex(/^[a-z][a-z0-9_]{1,40}$/, 'lower_snake_case, 2-41 characters');

export const CompetencySchema = z.object({
  key,
  label: z.string().min(2).max(80),
  /** What good looks like, used by both the interviewer and the scorer. */
  description: z.string().min(10).max(1200),
  /** A scenario or opening question the interviewer can adapt. */
  probe: z.string().max(800).optional(),
  weight: z.number().min(0).max(100),
});
export type Competency = z.infer<typeof CompetencySchema>;

export const FactTypeSchema = z.enum(['number', 'boolean', 'text']);

export const FactSchema = z.object({
  key,
  label: z.string().min(2).max(80),
  type: FactTypeSchema,
  /** e.g. "days", "LPA", "years" - numbers are normalised to this unit. */
  unit: z.string().max(20).optional(),
  /** How the interviewer should ask for it. */
  ask: z.string().min(5).max(400),
  required: z.boolean().default(true),
});
export type Fact = z.infer<typeof FactSchema>;

export const KnockoutOpSchema = z.enum([
  'lt',
  'lte',
  'gt',
  'gte',
  'eq',
  'neq',
  'is_true',
  'is_false',
  'contains',
  'not_contains',
]);
export type KnockoutOp = z.infer<typeof KnockoutOpSchema>;

export const KnockoutRuleSchema = z.object({
  /** Fact key the rule reads. */
  fact: key,
  /** The rule FAILS when `fact op value` is true. */
  op: KnockoutOpSchema,
  value: z.union([z.number(), z.string(), z.boolean()]).optional(),
  /** hard: fails the candidate outright. soft: forces a human review. */
  severity: z.enum(['hard', 'soft']),
  message: z.string().min(3).max(200),
});
export type KnockoutRule = z.infer<typeof KnockoutRuleSchema>;

/**
 * An email the candidate gets when the hiring team decides. Subject and body may use {first_name},
 * {job_title} and {company}; left empty, the default wording (messages.ts) is used.
 */
export const StatusEmailSchema = z.object({
  /** Ticked by default in the decision dialog. */
  send: z.boolean(),
  /** Hours to wait before sending; a changed decision in the meantime cancels it. */
  delayHours: z.number().int().min(0).max(168),
  subject: z.string().trim().max(200).optional(),
  body: z.string().trim().max(4000).optional(),
});
export type StatusEmail = z.infer<typeof StatusEmailSchema>;

export const JobMessagesSchema = z.object({
  /** Recruiters emailed when a candidate finishes the skills assessment or books or cancels a recruiter interview. Empty: whoever created the job. */
  notify: z.array(z.string().trim().toLowerCase().email()).max(10),
  reject: StatusEmailSchema,
  select: StatusEmailSchema,
});
export type JobMessages = z.infer<typeof JobMessagesSchema>;

export function defaultJobMessages(): JobMessages {
  return { notify: [], reject: { send: true, delayHours: 24 }, select: { send: true, delayHours: 0 } };
}

/**
 * Gemini Live voices a job can pick (all female). Gemini Live has no speed setting: the pace comes from
 * the voice and the prompt. Measured on the same interviewer lines (2026-10-02): Kore, the first voice
 * used, is among the fastest (18.6 s); Leda with the prompt's "speak slowly" took about 25 s.
 */
export const LIVE_VOICES = [
  { id: 'Leda', label: 'Leda: clear and unhurried (the slowest)' },
  { id: 'Sulafat', label: 'Sulafat: warm' },
  { id: 'Aoede', label: 'Aoede: bright, easy-going' },
  { id: 'Achernar', label: 'Achernar: soft' },
  { id: 'Vindemiatrix', label: 'Vindemiatrix: gentle' },
  { id: 'Kore', label: 'Kore: firm and quick' },
] as const;
export const DEFAULT_LIVE_VOICE = 'Leda';

/**
 * The job's public apply page (/apply/<job id>/<key>): a candidate adds themselves, with a resume, and
 * with autoStart is interviewed at once: a link to start in the browser, or a call within calling
 * hours. The key is derived from the version, so bumping it retires the old link.
 */
export const ApplySettingsSchema = z.object({
  enabled: z.boolean().default(false),
  autoStart: z.boolean().default(true),
  version: z.number().int().min(1).max(1000).default(1),
  /** A line the company wants on the page, besides the role description. */
  note: z.string().max(600).optional(),
});
export type ApplySettings = z.infer<typeof ApplySettingsSchema>;
export const defaultApplySettings = (): ApplySettings => ({ enabled: false, autoStart: true, version: 1 });

export const JobConfigSchema = z
  .object({
    companyName: z.string().min(1).max(80),
    interviewerName: z.string().min(1).max(40),
    competencies: z.array(CompetencySchema).min(1).max(12),
    facts: z.array(FactSchema).max(25),
    knockouts: z.array(KnockoutRuleSchema).max(25),
    thresholds: z
      .object({
        /** Overall score (0-100) at or above which a clean candidate qualifies. */
        qualify: z.number().min(0).max(100),
        /** Below this the candidate does not qualify; between the two is review. */
        review: z.number().min(0).max(100),
      })
      .refine((t) => t.review <= t.qualify, 'review threshold must not exceed qualify'),
    /** Languages the candidate may choose. The first is the default. */
    languages: z.array(z.enum(LANGUAGE_CODES as [LanguageCode, ...LanguageCode[]])).min(1),
    /**
     * Customer-facing English roles: run part of the interview in English
     * even when the candidate chose another language, and score it.
     */
    assessEnglish: z.boolean(),
    /** Minimum / maximum experience from the lead sheet, for pre-screening. */
    experienceRange: z
      .object({ min: z.number().min(0).max(60), max: z.number().min(0).max(60) })
      .optional(),
    /** Anything else the interviewer must know (salary band talk-track, etc.). */
    extraInstructions: z.string().max(3000).optional(),
    /**
     * How the interviewer hears and speaks. standard (the owner's choice, 2026-10-08): Sarvam
     * speech-to-text and voice around a text model. gemini-live: one Gemini Live model listens and speaks
     * itself; faster, but on real phone calls it sometimes did not take its turn, and it costs more.
     */
    voiceEngine: z.enum(['standard', 'gemini-live']).default('standard'),
    /** The interviewer's (and the assessor's) voice on Gemini Live; unset: DEFAULT_LIVE_VOICE. */
    liveVoice: z.enum(LIVE_VOICES.map((v) => v.id) as [string, ...string[]]).optional(),
    /** Stage 2: the AI skills assessment after a shortlist. Without it, shortlisting only records the decision. */
    assessment: AssessmentConfigSchema.optional(),
    /** Emails to the hiring team and status emails to candidates. */
    messages: JobMessagesSchema.default(defaultJobMessages),
    /** The public apply page; off unless the recruiter switches it on. */
    apply: ApplySettingsSchema.default(defaultApplySettings),
    /** The role-library template the job was built from, if any. */
    roleId: z.string().max(60).optional(),
  })
  .superRefine((cfg, ctx) => {
    const seen = new Set<string>();
    for (const c of cfg.competencies) {
      if (seen.has(c.key)) ctx.addIssue({ code: 'custom', message: `duplicate key ${c.key}` });
      seen.add(c.key);
    }
    const facts = new Set(cfg.facts.map((f) => f.key));
    if (facts.size !== cfg.facts.length) ctx.addIssue({ code: 'custom', message: 'duplicate fact key' });
    for (const k of cfg.knockouts) {
      if (!facts.has(k.fact)) {
        ctx.addIssue({ code: 'custom', message: `knockout refers to unknown fact "${k.fact}"` });
      }
    }
    const totalWeight = cfg.competencies.reduce((s, c) => s + c.weight, 0);
    if (totalWeight <= 0) ctx.addIssue({ code: 'custom', message: 'competency weights sum to zero' });
  });
export type JobConfig = z.infer<typeof JobConfigSchema>;

/**
 * Default template: Technical Support Associate (customer onboarding),
 * the role in the first lead sheet. Every value is editable per job.
 */
export function technicalSupportAssociateTemplate(companyName = 'Eduvy'): JobConfig {
  return {
    companyName,
    interviewerName: 'Aria',
    competencies: [
      {
        key: 'communication',
        label: 'Communication',
        description:
          'Explains clearly and in a structured way, listens to the question actually asked, keeps a ' +
          'professional and warm tone, and can simplify technical ideas for a non-technical customer. ' +
          'Assessed across the whole conversation, not from one answer.',
        weight: 25,
      },
      {
        key: 'troubleshooting',
        label: 'Technical troubleshooting',
        description:
          'Diagnoses a software problem methodically: gathers details, reproduces, isolates (user, ' +
          'account, browser, network, configuration, product bug), checks logs or admin settings, ' +
          'escalates with a clear reproduction when it is a bug, and closes the loop with the customer.',
        probe:
          'Scenario: a school administrator we onboarded yesterday says none of their teachers can log ' +
          'in this morning. Walk me through exactly what you would do.',
        weight: 25,
      },
      {
        key: 'customer_handling',
        label: 'Customer handling',
        description:
          'Empathy, ownership and de-escalation with a frustrated customer; sets honest expectations ' +
          'and follows up. Does not blame the customer or over-promise.',
        probe:
          'Scenario: a customer is angry because their data import failed twice and their go-live is ' +
          'tomorrow. How would you handle that call?',
        weight: 20,
      },
      {
        key: 'saas_onboarding',
        label: 'SaaS onboarding experience',
        description:
          'Real, specific experience onboarding customers onto a SaaS / ERP / CRM product: account ' +
          'setup, configuration, data migration or imports, user training, go-live support, ticketing ' +
          'tools (Freshdesk, Zendesk, Jira), SLAs. Specific examples score higher than general claims.',
        probe: 'Tell me about a customer you onboarded end to end. What did you configure, and what went wrong?',
        weight: 20,
      },
      {
        key: 'motivation_fit',
        label: 'Motivation and role fit',
        description:
          'Why this role and why now; reasons for leaving the current job; comfort with the work ' +
          'pattern (shifts, targets, customer calls); likely stability.',
        weight: 10,
      },
    ],
    facts: [
      { key: 'current_role', label: 'Current role', type: 'text', ask: 'Current designation and company.', required: true },
      { key: 'total_experience_years', label: 'Total experience', type: 'number', unit: 'years', ask: 'Total years of work experience.', required: true },
      { key: 'relevant_experience_years', label: 'Relevant experience', type: 'number', unit: 'years', ask: 'Years in technical support, customer onboarding or implementation.', required: true },
      { key: 'current_location', label: 'Current location', type: 'text', ask: 'City they live in now.', required: true },
      { key: 'willing_to_relocate', label: 'Open to the job location', type: 'boolean', ask: 'Whether they can work from the job location (ask only if they are elsewhere).', required: true },
      { key: 'notice_period_days', label: 'Notice period', type: 'number', unit: 'days', ask: 'Notice period in days, and whether it is negotiable.', required: true },
      { key: 'current_ctc_lpa', label: 'Current CTC', type: 'number', unit: 'LPA', ask: 'Current annual CTC in lakhs.', required: true },
      { key: 'expected_ctc_lpa', label: 'Expected CTC', type: 'number', unit: 'LPA', ask: 'Expected annual CTC in lakhs.', required: true },
      { key: 'shift_flexible', label: 'Open to rotational shifts', type: 'boolean', ask: 'Whether they are comfortable with rotational shifts, including occasional weekends.', required: true },
      // The skills assessment runs customer role-plays in the languages they speak, except their mother tongue.
      { key: 'languages_spoken', label: 'Languages spoken', type: 'text', ask: 'Every language they can comfortably hold a conversation in (list them all).', required: true },
      { key: 'mother_tongue', label: 'Mother tongue', type: 'text', ask: 'Their mother tongue (first language).', required: true },
      { key: 'ticketing_tools', label: 'Ticketing / CRM tools', type: 'text', ask: 'Ticketing or CRM tools they have used.', required: false },
    ],
    knockouts: [
      { fact: 'notice_period_days', op: 'gt', value: 60, severity: 'soft', message: 'Notice period over 60 days' },
      { fact: 'expected_ctc_lpa', op: 'gt', value: 7, severity: 'soft', message: 'Expected CTC above the 7 LPA band' },
      { fact: 'relevant_experience_years', op: 'lt', value: 1, severity: 'soft', message: 'Less than one year of relevant experience' },
      { fact: 'shift_flexible', op: 'is_false', severity: 'soft', message: 'Not open to rotational shifts' },
      { fact: 'willing_to_relocate', op: 'is_false', severity: 'hard', message: 'Cannot work from the job location' },
    ],
    thresholds: { qualify: 70, review: 55 },
    languages: ['en-IN', 'hi-IN', 'ml-IN', 'ta-IN', 'kn-IN', 'te-IN'],
    assessEnglish: true,
    experienceRange: { min: 1, max: 8 },
    voiceEngine: 'standard',
    assessment: technicalSupportAssessment(),
    messages: defaultJobMessages(),
    apply: defaultApplySettings(),
  };
}
